// The calendar's days, in the reader's zone (screens/CALENDAR.md, "Data").
//
// A DAY IS A DATE IN THE READER'S ZONE, never a stretch of 24 hours: the
// server serves instants (`CalendarView`), and which day each falls on is
// the reader's, as every time on the site is theirs (B-181). A day is named
// by its date key, `2026-11-01`, and begins at its zone's midnight, which a
// daylight-saving change moves (`instantOfLocal`).
//
// THE LAYOUTS: two weeks or one that start on Sunday, as a calendar does, or
// two of the league's own weeks, starting on the day its week does, which is
// read from the league's weeks and never assumed (DIRECTION U30).

import { instantOfLocal, partsIn } from '@fsp/clock';
import type { CalendarWeek } from '@fsp/domain';
import type { CalendarLayout } from '../../data/calendarLayout';

const DAY_MS = 24 * 60 * 60 * 1000;

/** `2026-11-01`: the date `at` falls on in `zone`. */
export function dayKeyOf(at: number, zone: string): string {
  const p = partsIn(zone, new Date(at));
  return keyOf(p.year, p.month, p.day);
}

function keyOf(year: number, month: number, day: number): string {
  return `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function partsOf(key: string): [number, number, number] {
  const [y, m, d] = key.split('-').map(Number);
  return [y!, m!, d!];
}

/** The date `n` days from `key`, by the calendar, whatever the clocks do. */
export function addDays(key: string, n: number): string {
  const [y, m, d] = partsOf(key);
  const at = new Date(Date.UTC(y, m - 1, d) + n * DAY_MS);
  return keyOf(at.getUTCFullYear(), at.getUTCMonth() + 1, at.getUTCDate());
}

/** 0 for Sunday. */
export function weekdayOf(key: string): number {
  const [y, m, d] = partsOf(key);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** The day's date in the month: `1` for the first. */
export function dateOf(key: string): number {
  return partsOf(key)[2];
}

/** When `key` begins in `zone`: its midnight. */
export function dayStart(key: string, zone: string): number {
  const [y, m, d] = partsOf(key);
  return instantOfLocal(zone, y, m, d, 0, 0).getTime();
}

/** The latest day on or before `key` that is the given weekday. */
export function weekStartOf(key: string, weekday: number): string {
  return addDays(key, -((weekdayOf(key) - weekday + 7) % 7));
}

/**
 * The day the league's week starts on, read from its weeks: the first day of
 * its week under way (or its first) in the reader's zone, by the rule that
 * places every day (`weekOfDay`, its midday) — so where a week turns over
 * after noon, as a football week does in London, the row starts the day
 * after, and its band does not break after its first day. Sunday when the
 * league has no weeks yet, so the layout is a calendar's.
 */
export function leagueWeekday(weeks: readonly CalendarWeek[], zone: string, now: number): number {
  const week =
    weeks.find((w) => Date.parse(w.start) <= now && now < Date.parse(w.end)) ?? weeks[0] ?? null;
  if (week === null) return 0;
  const start = Date.parse(week.start);
  const key = dayKeyOf(start, zone);
  return weekdayOf(dayStart(key, zone) + DAY_MS / 2 < start ? addDays(key, 1) : key);
}

/** A phone's strip: the seven days from the latest `weekday` on or before `anchor`. */
export function stripOf(anchor: string, weekday: number): string[] {
  const start = weekStartOf(anchor, weekday);
  return Array.from({ length: 7 }, (_, c) => addDays(start, c));
}

/** How many rows of seven a layout draws. */
export function rowsOf(layout: CalendarLayout): number {
  return layout === 'one-week' ? 1 : 2;
}

/**
 * The days a layout draws around `anchor`, row by row: rows that start on
 * Sunday, or on the league's week day for the fantasy week, the first holding
 * `anchor`.
 */
export function gridOf(anchor: string, layout: CalendarLayout, weekday: number): string[][] {
  const start = weekStartOf(anchor, layout === 'fantasy-week' ? weekday : 0);
  return Array.from({ length: rowsOf(layout) }, (_, r) =>
    Array.from({ length: 7 }, (_, c) => addDays(start, r * 7 + c)),
  );
}

/** The instants a run of days covers: from the first's midnight to the midnight after the last. */
export function rangeOf(days: readonly string[], zone: string): { from: number; to: number } {
  return { from: dayStart(days[0]!, zone), to: dayStart(addDays(days[days.length - 1]!, 1), zone) };
}

/**
 * The league's week a day belongs to: the one whose span holds the day's
 * midday, so the day a week turns over belongs to the week it starts.
 */
export function weekOfDay(
  key: string,
  zone: string,
  weeks: readonly CalendarWeek[],
): CalendarWeek | null {
  const midday = dayStart(key, zone) + DAY_MS / 2;
  return weeks.find((w) => Date.parse(w.start) <= midday && midday < Date.parse(w.end)) ?? null;
}

/** A row's run of days in one of the league's weeks: where its band starts and how far it spans. */
export interface Band {
  week: CalendarWeek;
  /** The column it starts in, from 0. */
  from: number;
  span: number;
}

/** The bands under a row's dates: each run of days in one week, breaking where the week does. */
export function bandsOf(
  row: readonly string[],
  zone: string,
  weeks: readonly CalendarWeek[],
): Band[] {
  const out: Band[] = [];
  row.forEach((key, i) => {
    const week = weekOfDay(key, zone, weeks);
    if (week === null) return;
    const last = out[out.length - 1];
    if (last !== undefined && last.week.period === week.period && last.from + last.span === i)
      last.span += 1;
    else out.push({ week, from: i, span: 1 });
  });
  return out;
}

/** Each day's entries, in the order given, by the day each falls on. */
export function byDayOf<T extends { at: number }>(
  items: readonly T[],
  zone: string,
): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const item of items) {
    const key = dayKeyOf(item.at, zone);
    out.set(key, [...(out.get(key) ?? []), item]);
  }
  return out;
}
